'use client';

import { Dialog, DialogContent, DialogTitle } from '@ophelos/ui/components/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@ophelos/ui/components/tabs';
import { CreditCard, Landmark } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { DebtPageHeader } from '@/components/layout/DebtPageHeader';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { Drawer, DrawerContent, DrawerTitle } from '@ophelos/ui/components/drawer';
import { MultibancoReference } from '@/components/payments/MultibancoReference';
import { useTabLinkWhileOpen } from '@/hooks/useTabLinkWhileOpen';
import { usePayerEmail } from '@/hooks/usePayerEmail';
import { useConfiguration } from '@/contexts/ConfigurationContext';
import { useDebtData } from '@/contexts/DebtDataContext';
import { isOnlyOpenDebt } from '@/lib/debtFilters';
import { useAuth } from '@/contexts/customer-auth-context';
import { usePreviewNavigate } from '@/hooks/usePreviewNavigate';
import { useTranslation } from '@/hooks/useTranslation';
import { Loader } from '@ophelos/ui/components/loader';
import { ConnectApiError, mintBalanceReferenceOnConnect, updatePaymentSessionStatusOnConnect } from '@/lib/connect-api';
import { mintFailureAction, noMintCopyKey, shouldServeSharedReference } from '@/lib/balance-reference';
import { planRefusalReason } from '@/lib/planRefusal';
import { useBalanceReference } from '@/hooks/useBalanceReference';
import { type MultibancoReference as MultibancoReferenceType, PaymentApiError, usePaymentApi } from '@/lib/payment-api';
import { accountPaymentPath } from '@/lib/accountPayment';
import { toSubunits } from '@/lib/currency';
import { buildPlanSchedule, buildScheduleForDate, toApiSchedule, toScheduleDate } from '@/lib/payment-schedule';
import { paymentReturnUrl } from '@/lib/paymentReturnUrl';
import { planDeepLink } from '@/lib/redirect';
import { methodDateAvailability, SCHEDULING_MIN_LEAD_DAYS, type PaymentMethod } from '@ophelos/shared/constants/payment-methods';
import { paymentMethods } from '@/components/payments/payment-method-constants';
import { activeAccountPayment, isScheduledPayment, scheduledPaymentAmount, scheduledPaymentDate, scheduledPaymentMethod } from '@/lib/paymentPlans';
import { type PaymentTab, paymentTabsOnOffer, resolveActivePaymentTab } from '@/lib/paymentTabs';
import { useActivePaymentSession } from '@/hooks/usePaymentSessionBlocking';
import { usePaymentMethodAvailability } from '@/hooks/usePaymentMethodAvailability';
import { getPaymentTabVisibility } from '@/lib/payment-blocking';
import { DebtBreakdownTab } from './_partials/_shared/DebtBreakdownTab';
import { TransactionsList, filterCustomerTransactions } from './_partials/_shared/TransactionsList';
import { useCaseReference } from '@/hooks/useCaseReference';
import { PartialPaymentTab } from './_partials/_singlePaymentPartials/PartialPaymentTab';
import { PayInFullTab } from './_partials/_singlePaymentPartials/PayInFullTab';
import { PaymentOptionView } from './_partials/_singlePaymentPartials/PaymentOptionView';
import { PaymentMethodTab } from './_partials/_singlePaymentPartials/PaymentMethodTab';
import { PaymentPlanTab, type PaymentPlanData } from './_partials/_singlePaymentPartials/PaymentPlanTab';
import { PlanAuthoriseSheet } from './_partials/_singlePaymentPartials/PlanAuthoriseSheet';
import { AccountPaymentBlock } from './_partials/_singlePaymentPartials/AccountPaymentBlock';
import { ScheduledPaymentPanel } from './_partials/_singlePaymentPartials/ScheduledPaymentPanel';
import { useIsMobile } from '@/hooks/use-mobile';

// A future-dated payment registers the card/bank account for a later charge —
// only automatic-plan methods support that (Multibanco/MB Way cannot be scheduled).
const SCHEDULABLE_METHODS: PaymentMethod[] = ['card', 'sepa', 'bacs'];

const ALL_PAYMENT_METHODS: PaymentMethod[] = paymentMethods.map((m) => m.value);

/**
 * Methods offered on this screen for the given date. With no date (immediate
 * payment) that's every method with no lead-time floor at all — Bacs can
 * never be paid today, so it isn't a candidate to show disabled either.
 * Once a date is picked, every schedulable method is a candidate; whichever
 * ones don't clear their lead-time floor (see SCHEDULING_MIN_LEAD_DAYS) come
 * back from `methodDateAvailability` as unavailable rather than being
 * dropped from the list.
 */
function candidateMethodsForDate(date: Date | undefined): PaymentMethod[] {
  return date ? SCHEDULABLE_METHODS : ALL_PAYMENT_METHODS.filter((m) => SCHEDULING_MIN_LEAD_DAYS[m] === undefined);
}

export default function Payment() {
  const { debtId } = useParams<{ debtId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const previewNavigate = usePreviewNavigate();
  const { config, isPreviewMode } = useConfiguration();
  const { t, currentLanguage } = useTranslation();
  const { accountId, authStage, getAccessToken } = useAuth();
  const { createPaymentSession, cancelPaymentPlan } = usePaymentApi();
  const { debts, isLoading, activeOffers, offersLoading, refreshDebts, accountPaymentPlan } = useDebtData();
  const { needsEmail, payerEmail, setPayerEmail, emailValid, persistPayerEmail } = usePayerEmail();
  const isMobile = useIsMobile();
  const balanceReference = useBalanceReference();
  const { data: methodConfigAvailability } = usePaymentMethodAvailability();
  // Find debt from cached data
  const debt = useMemo(() => {
    if (!debtId) return null;
    return debts.find((d) => d.id === debtId) || null;
  }, [debts, debtId]);

  // The shared code is bound to the whole ACCOUNT balance, but this page pays a
  // single debt. Reusing it here is only honest when this debt IS the account's
  // whole payable balance; with other debts open, pay-in-full by Multibanco
  // stays on the per-debt mint and the shared code is offered on the
  // account-level page instead.
  const coversWholeAccountBalance = useMemo(() => isOnlyOpenDebt(debts, debtId), [debts, debtId]);

  // Query active payment session for blocking logic
  const { activeSession } = useActivePaymentSession(debt?.id, getAccessToken);

  // Compute tab visibility based on active session
  const tabVisibility = useMemo(() => getPaymentTabVisibility(activeSession), [activeSession]);

  // Whether the operator offers a plan or a part payment on this debt at all — a different
  // question from `tabVisibility`, which only reflects a payment session already in flight.
  // Resolved here rather than beside its render use below, because that sits after the loading
  // guards and the auto-switch effect has to weigh it: a `?tab=plan` or `?tab=partial` link
  // naming a tab this operator does not offer would otherwise stay selected with neither
  // trigger nor body rendered.
  const onOffer = debt && paymentTabsOnOffer(debt.currentBalance, config.payment);
  const planTabOnOffer = !!onOffer?.plan;
  const partialTabOnOffer = !!onOffer?.partial;

  // Filter transactions to only show customer-relevant ones (payments, refunds)
  const customerTransactions = useMemo(() => filterCustomerTransactions(debt?.transactions ?? []), [debt?.transactions]);


  // Bound once for the screen, and above the loading/not-found returns below: a call
  // after those returns changes the hook count between renders.
  const caseReference = useCaseReference(debt?.id);

  // All useState hooks must be called unconditionally at top level.
  // Initial tab + prefill come from an operator-sent deep link (?tab=&...).
  // Captured once so the later URL cleanup doesn't drop them.
  const [paymentTab, setPaymentTab] = useState<'full' | 'partial' | 'plan'>(() => {
    const tab = searchParams.get('tab');
    return tab === 'partial' || tab === 'plan' ? tab : 'full';
  });
  const [linkPrefill] = useState(() => ({
    frequency: searchParams.get('frequency'),
    start: searchParams.get('start'),
    count: searchParams.get('count'),
    amount: searchParams.get('amount'),
    // Set by the budget assessment when it hands the customer back to the plan they were
    // configuring. Captured here rather than read in the tab so the cleanup below strips it —
    // otherwise it survives refreshes and Back.
    offer: searchParams.get('offer'),
  }));

  // Auto-switch to available tab if current tab gets blocked. Held until the debt has
  // loaded, because `planTabOnOffer` reads its balance and would switch a deep link away
  // from the plan tab before the terms it carries could be applied.
  useEffect(() => {
    if (!tabVisibility || isLoading || !debt) return;

    const next = resolveActivePaymentTab(paymentTab, {
      full: tabVisibility.canPayFull,
      partial: tabVisibility.canPayPartial && partialTabOnOffer,
      plan: tabVisibility.canCreatePlan && planTabOnOffer,
    });
    if (next !== paymentTab) setPaymentTab(next);
  }, [tabVisibility, paymentTab, planTabOnOffer, partialTabOnOffer, isLoading, debt]);

  // Handle redirect back from Ophelos after cancelled/abandoned payment.
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId && sessionId.startsWith('psess_')) {
      // Update session status to 'incomplete' (unlocks payment, customer can retry)
      const updateSessionStatus = async () => {
        try {
          await updatePaymentSessionStatusOnConnect(sessionId, 'incomplete', authStage, getAccessToken);
        } catch (err) {
          console.error('[Payment] Unexpected error updating session status:', {
            session_id: sessionId,
            error: err,
          });
          // Non-fatal: Continue showing payment page normally
        }
      };

      updateSessionStatus();

      // Clear session_id from URL
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('session_id');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams, authStage, getAccessToken]);

  // Strip deep-link prefill params from the URL once captured, so a refresh or
  // share doesn't re-apply stale values (mirrors the session_id cleanup above).
  useEffect(() => {
    const keys = ['tab', 'frequency', 'start', 'count', 'amount', 'offer'];
    if (!keys.some((k) => searchParams.has(k))) return;
    const newParams = new URLSearchParams(searchParams);
    for (const k of keys) newParams.delete(k);
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const [mainTab, setMainTab] = useState<'payment' | 'breakdown'>(() => (debt?.currentBalance != 0 ? 'payment' : 'breakdown'));
  // The debt can arrive after mount, and a cleared one has no tab bar to switch with.
  const nothingToPay = debt != null && debt.currentBalance == 0;
  useEffect(() => {
    if (nothingToPay) setMainTab('breakdown');
  }, [nothingToPay]);
  useTabLinkWhileOpen(
    useCallback((tab: PaymentTab) => {
      setPaymentTab(tab);
      setMainTab('payment');
    }, []),
  );
  const [paymentAmount, setPaymentAmount] = useState<number | null>(null);
  const [paymentType, setPaymentType] = useState<'full' | 'partial'>('full');
  // Set when the customer picks a future date; the payment is then taken as a
  // single-installment plan rather than an immediate charge.
  const [paymentDate, setPaymentDate] = useState<Date | undefined>(undefined);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('card');
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [isCancellingScheduled, setIsCancellingScheduled] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [multibancoRef, setMultibancoRef] = useState<MultibancoReferenceType | null>(null);
  // Plan steps: configure (the tab) → choose a method → review (card only) → authorise.
  const [showPlanReview, setShowPlanReview] = useState(false);
  const [reviewPlanData, setReviewPlanData] = useState<PaymentPlanData | null>(null);
  // The verdict, held apart from the prefill above because the two have opposite lifetimes:
  // `reviewPlanData` must survive "Edit plan" to re-hydrate the form, while a verdict must not
  // survive a fresh submission that may have superseded it.
  const [decidedPlan, setDecidedPlan] = useState<PaymentPlanData | null>(null);
  // Why the customer was sent back from review, shown on the setup tab they land on.
  const [planReturnNotice, setPlanReturnNotice] = useState<string | null>(null);

  // Memoize dashboard URL to avoid duplication
  const dashboardUrl = useMemo(() => (isPreviewMode ? '/dashboard?preview=true' : '/dashboard'), [isPreviewMode]);

  // methodDateAvailability anchors its own "today" to the UTC calendar day
  // (see payment-methods.ts) — this key forces a recompute once that day
  // rolls over, the same technique usePlanMethodAvailability uses, so a tab
  // left open across UTC midnight doesn't keep checking a stale date.
  const todayUtcDayKey = new Date().toISOString().slice(0, 10);
  const methodAvailability = useMemo(
    () => methodDateAvailability(candidateMethodsForDate(paymentDate), paymentDate ?? new Date()),
    [paymentDate, todayUtcDayKey],
  );
  // Candidates that don't clear their lead-time floor stay in the list but
  // render disabled, with this as the reason on their own row.
  const methodDisabledReasons = useMemo(() => {
    const reasons: Partial<Record<PaymentMethod, string>> = {};
    for (const method of candidateMethodsForDate(paymentDate)) {
      const a = methodAvailability[method];
      if (a && !a.available && a.availableFrom) {
        reasons[method] = t('paymentMethod.unavailableUntilDate', {
          date: a.availableFrom.toLocaleDateString(currentLanguage, { month: 'short', day: 'numeric' }),
        });
      }
    }
    return reasons;
  }, [paymentDate, methodAvailability, t, currentLanguage]);
  const methodUsable =
    candidateMethodsForDate(paymentDate).includes(paymentMethod) &&
    !!methodConfigAvailability[paymentMethod] &&
    !methodDisabledReasons[paymentMethod];

  // The date determines which methods are selectable (e.g. Bacs needs a date far enough
  // out, and can never be immediate); the operator's own config determines which methods
  // exist at all — a change has to clear both bars, not just the date one.
  useEffect(() => {
    const isSelectable = (m: PaymentMethod) => methodConfigAvailability[m] && (methodAvailability[m]?.available ?? false);
    if (isSelectable(paymentMethod)) return;
    const firstSelectable = candidateMethodsForDate(paymentDate).find(isSelectable);
    if (firstSelectable) setPaymentMethod(firstSelectable);
  }, [paymentDate, methodAvailability, methodConfigAvailability, paymentMethod]);

  // The amount tabs report what they show as it changes; the charge reads it from here.
  const handleSelectPaymentMethod = useCallback((amount: number, type: 'full' | 'partial', date?: Date) => {
    setPaymentAmount(amount);
    setPaymentType(type);
    setPaymentDate(date);
    setPaymentError(null);
  }, []);

  // The picker hides any method whose provider is off, so the selection must never hold one.
  const planMethods = useMemo(
    () => ((config.payment.planPaymentMethods ?? []) as PaymentMethod[]).filter((m) => methodConfigAvailability[m]),
    [config.payment.planPaymentMethods, methodConfigAvailability],
  );

  // Redirect if not authenticated or no debtId
  useEffect(() => {
    if (!accountId && !isPreviewMode) {
      previewNavigate('/login');
      return;
    }

    if (!debtId) {
      previewNavigate(dashboardUrl);
      return;
    }
  }, [accountId, debtId, previewNavigate, isPreviewMode, dashboardUrl]);

  // Show loading state while data is being fetched
  if (isLoading) {
    return (
      <Layout showFooter={false}>
        <div className="flex items-center justify-center min-h-screen">
          <Loader fullScreen />
        </div>
      </Layout>
    );
  }

  // Show error if debt not found
  if (!debt) {
    return (
      <Layout showFooter={false}>
        <Breadcrumb />
        <div className="flex items-center justify-center min-h-screen">
          <p className="text-destructive">{t('payment.debtNotFound')}</p>
        </div>
      </Layout>
    );
  }

  // Check if partial payment should be enabled based on debt amount
  // Same predicate the auto-switch above weighs, `partialPaymentTab` included — the render
  // gates used to re-derive two thirds of it and drop the third.
  const isPartialPaymentEnabled = partialTabOnOffer;

  // Surface the live plan discount on the plan button. Partial payment is excluded by
  // product — discounts never apply to partials.
  const activeOffer = activeOffers[debt.id];
  const offerIsLive = activeOffer != null && activeOffer.status !== 'expired' && activeOffer.status !== 'superseded';
  const payNowOpt = activeOffer?.configSnapshot.options?.pay_now;
  const planOpt = activeOffer?.configSnapshot.options?.payment_plan;
  const planDiscountLabel = offerIsLive && planOpt?.enabled ? t('discount.percentOff', { pct: Math.round(planOpt.percentage * 100) }) : null;

  // A future-dated payment is a single-instalment plan, not an ATM payment.
  const isScheduled = !!paymentDate;

  // A pay-now discount prices this payment below the balance the shared code is
  // bound to. Never applies to a partial or a scheduled payment.
  const discountApplies = paymentType === 'full' && offerIsLive && !!payNowOpt?.enabled && !isScheduled;

  // While the offer lookup is in flight an absent offer means "not known yet",
  // not "none". Reading it as "none" issues a code bound to the undiscounted
  // balance, and nothing can withdraw that code once the lookup resolves to a
  // live offer — so the unknown has to count as "a discount may apply".
  const discountMayApply = offersLoading || discountApplies;

  // One predicate for both the card and the charge. Deriving it twice is how the
  // card came to be shown for a discounted payment the charge correctly refused.
  const canServeSharedReference = shouldServeSharedReference({
    paymentMethod,
    isFullPayment: paymentType === 'full',
    isScheduled,
    hasDiscount: discountMayApply,
    coversWholeAccountBalance,
  });

  // A part payment by Multibanco is minted as a case code: bound to its own amount and this
  // case's balance, so the server can price a live pay-now discount in at the ceiling — which
  // the per-checkout mint never does for a partial. A future date never offers Multibanco.
  const mintsCaseReference = paymentMethod === 'multibanco' && paymentType === 'partial' && !isScheduled;
  // The live code this case already holds for exactly this amount, shown beside the method so a
  // customer returning for it need not press pay. Withheld while a pay-now discount may apply,
  // as the account code is: reuse also requires the frozen discount to match what the mint
  // would price now, which the read cannot tell us, so a held code could carry a stale price.
  const partialDiscountMayApply = offersLoading || (offerIsLive && !!payNowOpt?.enabled);
  const heldCaseReference =
    mintsCaseReference && !partialDiscountMayApply && paymentAmount ? caseReference.partialFor(toSubunits(paymentAmount)) : null;

  // Same predicate the auto-switch above weighs; deriving it twice is how the tab came to
  // be selectable by a deep link while its trigger was withheld.
  const isPaymentPlanEnabled = planTabOnOffer;

  // A future-dated payment is already scheduled on this debt — block further
  // payments and show the scheduled state until it's taken or cancelled.
  const hasScheduledPayment = isScheduledPayment(debt.paymentPlan);
  // Blocks every case on the account, including ones added after it started —
  // membership is deliberately not consulted.
  const accountPayment = activeAccountPayment(accountPaymentPlan);
  const scheduledDate = scheduledPaymentDate(debt.paymentPlan);
  const scheduledAmount = scheduledPaymentAmount(debt.paymentPlan);
  const scheduledMethod = scheduledPaymentMethod(debt.paymentPlan);

  const handleCancelScheduledPayment = async () => {
    const planId = debt.paymentPlan?.externalPaymentPlanId;
    if (!planId) return;
    setIsCancellingScheduled(true);
    setPaymentError(null);
    try {
      await cancelPaymentPlan(planId);
      await refreshDebts();
      previewNavigate('/dashboard?scheduled_cancelled=true');
    } catch (err) {
      setPaymentError(err instanceof PaymentApiError ? err.message : t('payment.cancelScheduledFailed'));
    } finally {
      setIsCancellingScheduled(false);
    }
  };

  // A card plan advances to the review step, holding the configured plan so "Edit plan" can
  // return to setup with it prefilled; every other method sets the plan up from here.
  const handlePlanConfigured = (planData: PaymentPlanData) => {
    setPlanReturnNotice(null);
    setReviewPlanData(planData);
    setDecidedPlan(planData);
    setPaymentError(null);
    if (planData.paymentMethod === 'card') setShowPlanReview(true);
    else if (planData.paymentMethod) executePlan(planData.paymentMethod, planData);
  };

  const handleEditPlan = () => {
    // Keep reviewPlanData so the setup form re-hydrates from it (no reset).
    setShowPlanReview(false);
    setPaymentError(null);
  };

  // Create the plan session: card from the review screen, every other method straight from the
  // plan tab. The offer was already submitted and accepted on the way in, so nothing is decided here.
  const executePlan = async (method: PaymentMethod, plan = reviewPlanData) => {
    if (!plan || !debt) return;
    const { startDate, frequency, paymentCount, paymentAmount, totalAmount: planTotal } = plan;

    // Neither the discount pricing nor the balance is revalidated while review is open, so both
    // can go stale underneath it. `planRefusalReason` owns the decision so the two checks stay
    // independent — nesting one inside the other narrows the guard to the case where both apply.
    //
    // Say why, or pressing Authorise reads as a broken button: the setup screen just reappears
    // with a different total and no explanation. Carried as its own notice because
    // `handleEditPlan` clears the error slot on the way back.
    const refusal = planRefusalReason(plan, debt.currentBalance, activeOffers);
    if (refusal) {
      setPlanReturnNotice(t(refusal === 'offer-expired' ? 'countdown.offerExpired' : 'paymentPlan.review.balanceChanged'));
      handleEditPlan();
      return;
    }

    setIsProcessingPayment(true);
    setPaymentError(null);

    try {
      const baseUrl = window.location.origin;
      const schedule = toApiSchedule(buildPlanSchedule(planTotal, paymentCount, startDate, frequency));
      const response = await createPaymentSession({
        case_id: debt.id,
        currency: debt.currency,
        payment_method: method,
        payment_type: 'payment_plan',
        description: `Payment plan: ${paymentCount} ${frequency} payments of ${paymentAmount.toFixed(2)} ${debt.currency}`,
        success_url: paymentReturnUrl({ flow: 'plan', scope: debt.id }),
        cancel_url: `${baseUrl}${planDeepLink({ basePath: `/payment/${debt.id}`, frequency, startDate, count: paymentCount })}`,
        payment_plan_schedule: schedule,
        payment_plan_frequency: frequency,
      });

      if (response.checkout_url) {
        window.location.href = response.checkout_url;
      } else if (response.payment_plan_id) {
        // Manual plan (Multibanco): the plan record is written async via webhook, the dashboard polls for it.
        const sid = response.session_id ? `&session_id=${response.session_id}` : '';
        previewNavigate(isPreviewMode ? '/dashboard?preview=true' : `/dashboard?plan_success=${debt.id}${sid}`);
      } else {
        throw new Error('Invalid payment response: no checkout URL or plan ID');
      }
    } catch (err) {
      setIsProcessingPayment(false);
      setPaymentError(err instanceof PaymentApiError ? err.message : t('common.unexpectedError'));
      console.error('Plan session creation failed:', err);
    }
  };

  const handleProcessPayment = () => {
    if (!paymentAmount || !debt) return;
    if (!emailValid) {
      setPaymentError(t('paymentPlan.emailRequired'));
      return;
    }

    // Stage-2 verification is removed — proceed with Stage-1 payment.
    executePayment();
  };

  const executePayment = async () => {
    if (!paymentAmount || !debt) return;

    setIsProcessingPayment(true);
    setPaymentError(null);

    try {
      // Build redirect URLs. When a discount took effect (`payNow` on a live
      // offer for a full payment), thread the savings through to the dashboard
      // so the success banner can call out exactly how much the customer saved.
      const baseUrl = window.location.origin;
      // A discount applies only on full case-level payments with a live, enabled pay_now offer.
      // Partial payments never carry a discount.
      // A future-dated payment is taken as a single-installment plan; the
      // pay-now discount never applies to it.
      const savedAmount = discountApplies && debt.currentBalance > paymentAmount ? Math.max(0, debt.currentBalance - paymentAmount) : 0;
      const successUrl = isScheduled
        ? paymentReturnUrl({ flow: 'scheduled', scope: debt.id, date: toScheduleDate(paymentDate!) })
        : paymentReturnUrl({ flow: 'immediate', scope: debt.id, saved: savedAmount });
      const cancelUrl = `${baseUrl}/payment/${debt.id}`; // Redirect back to payment page for this case

      // Forward the offer id only when the discount applies and we actually have an id.
      const discountOfferId = discountApplies ? activeOffer?.offerId : undefined;

      // Persist a customer-supplied email (when none is usable on file) before checkout;
      // the server then resolves it from file. No-op when no email is needed.
      await persistPayerEmail();

      // Paying the whole balance by Multibanco reuses the account's shared code
      // instead of minting a new one per click, so the customer keeps seeing the
      // same reference. A discount prices the payment below that balance, so it
      // keeps the per-checkout mint. 404 means the feature is off for this
      // client (or there is no account context) — fall through to that path too.
      if (canServeSharedReference) {
        try {
          const minted = await mintBalanceReferenceOnConnect(authStage, getAccessToken);
          setIsProcessingPayment(false);
          if (minted.reference) {
            setMultibancoRef(minted.reference);
            balanceReference.refetch();
            return;
          }
          // No reference: name the empty state. 'in_arrangement' and
          // 'balance_changed' both still owe money.
          setPaymentError(t(noMintCopyKey(minted.reason)));
          return;
        } catch (err) {
          if (!(err instanceof ConnectApiError)) throw err;
          const action = mintFailureAction(err.status);
          if (action === 'rethrow') throw err;
          if (action === 'tell-customer-to-retry') {
            setIsProcessingPayment(false);
            setPaymentError(t('payment.referenceInProgress'));
            return;
          }
          // fall-back-to-legacy: the per-checkout mint below.
        }
      }

      if (mintsCaseReference) {
        // An unresolved offer is unknown, not absent. Falling through to checkout here would
        // mint a second kind of code beside any case code already live, and neither can be
        // withdrawn — so ask for a retry instead.
        if (offersLoading) {
          setIsProcessingPayment(false);
          setPaymentError(t('payment.referenceInProgress'));
          return;
        }
        try {
          const minted = await caseReference.mintPartial(toSubunits(paymentAmount));
          setIsProcessingPayment(false);
          if (minted.reference) {
            setMultibancoRef(minted.reference);
            return;
          }
          setPaymentError(t(noMintCopyKey(minted.reason, 'case')));
          return;
        } catch (err) {
          if (!(err instanceof ConnectApiError)) throw err;
          const action = mintFailureAction(err.status);
          if (action === 'rethrow') throw err;
          if (action === 'tell-customer-to-retry') {
            setIsProcessingPayment(false);
            setPaymentError(t('payment.referenceInProgress'));
            return;
          }
          // fall-back-to-legacy: case codes are off for this client, so none can exist for the
          // per-checkout mint below to duplicate.
        } finally {
          // Every outcome, not just success: the mint deadline can fire while the provider call
          // completes, leaving a real payable code the failed request never saw. The read never
          // mints, so re-reading is always safe.
          caseReference.refetch();
        }
      }

      // Create payment session (client_id is fetched internally by RPC)
      const kind = paymentType === 'partial' ? 'Partial' : 'Full';
      const whenLabel = isScheduled ? ` on ${paymentDate!.toLocaleDateString()}` : '';

      const response = await createPaymentSession({
        case_id: debt.id,
        amount_subunits: toSubunits(paymentAmount),
        currency: debt.currency,
        payment_method: paymentMethod,
        payment_type: paymentType,
        description: `${kind} payment of ${paymentAmount.toFixed(2)} ${debt.currency}${whenLabel}`,
        success_url: successUrl,
        cancel_url: cancelUrl,
        discount_offer_id: discountOfferId,
        payment_plan_schedule: isScheduled ? buildScheduleForDate(paymentAmount, paymentDate!) : undefined,
      });

      setIsProcessingPayment(false);

      // Handle response based on payment method
      if (response.multibanco_reference) {
        // Multibanco: Show reference code in modal
        setMultibancoRef(response.multibanco_reference);
      } else if (response.checkout_url) {
        // Card/SEPA: Redirect to Ophelos checkout
        window.location.href = response.checkout_url;
      } else {
        throw new Error('Invalid payment response: no checkout URL or Multibanco reference');
      }
    } catch (err) {
      setIsProcessingPayment(false);
      if (err instanceof PaymentApiError) {
        setPaymentError(err.message);
      } else {
        setPaymentError(t('common.unexpectedError'));
      }
      console.error('Payment session creation failed:', err);
    }
  };

  const methodSelector = (
    <PaymentMethodTab
      paymentMethod={paymentMethod}
      onSelectMethod={setPaymentMethod}
      disabled={isProcessingPayment}
      error={paymentError}
      availableMethods={candidateMethodsForDate(paymentDate)}
      disabledReasons={methodDisabledReasons}
      canServeSharedReference={canServeSharedReference}
      balanceReference={balanceReference.reference}
      heldCaseReference={heldCaseReference}
      excludesArrangedDebts={balanceReference.excludesArrangedDebts}
    />
  );

  return (
    <Layout showFooter={false}>
      {/* The payment options pin their affordability notice to the bottom of the option, which needs
          a continuous flex-grow chain from here down — a percentage-height chain resolves to `auto`
          through a `flex-basis: 0%` ancestor and collapses. */}
      <div className="mx-auto flex-1 w-full flex flex-col min-h-0">
        <Tabs variant="default" value={mainTab} onValueChange={(v) => setMainTab(v as 'payment' | 'breakdown')} className="space-y-2 flex-1 min-h-0">
          <DebtPageHeader backTo={dashboardUrl} creditor={debt}>
            {/* A cleared debt has only its details, and a lone tab is not worth a bar. */}
            {debt.currentBalance != 0 && (
              <TabsList className="max-w-lg w-full mx-auto max-sm:h-auto">
                <TabsTrigger value="payment" className="rounded-full max-xs:text-xs">
                  <CreditCard className="size-4 max-xs:hidden text-muted-foreground" />
                  {t('payment.payment')}
                </TabsTrigger>
                <TabsTrigger value="breakdown" className="rounded-full max-xs:text-xs">
                  <Landmark className="size-4 max-xs:hidden text-muted-foreground" />
                  {t('payment.details')}
                </TabsTrigger>
              </TabsList>
            )}
          </DebtPageHeader>

          <TabsContent value="payment" className="space-y-4 w-full mx-auto flex flex-col min-h-0">
            {accountPayment ? (
              <AccountPaymentBlock variant={accountPayment} onView={() => previewNavigate(accountPaymentPath(accountPayment))} />
            ) : hasScheduledPayment ? (
              // A payment is already scheduled on this debt — block new payments
              // and let the customer cancel the scheduled one.
              <ScheduledPaymentPanel
                amount={scheduledAmount}
                currency={debt.currency}
                scheduledDate={scheduledDate}
                paymentMethod={scheduledMethod}
                onCancel={handleCancelScheduledPayment}
                isCancelling={isCancellingScheduled}
                error={paymentError}
              />
            ) : paymentTab === 'plan' && isPaymentPlanEnabled && tabVisibility.canCreatePlan ? (
              // The plan and the part payment each take over the panel below the page tabs;
              // paying in full is the resting view they return to.
              <PaymentOptionView data-testid="payment-view-plan" title={t('payment.paymentPlan')} onClose={tabVisibility.canPayFull ? () => setPaymentTab('full') : undefined}>
                <PaymentPlanTab
                  caseId={debt.id}
                  totalAmount={debt.currentBalance}
                  creditApplied={debt.creditApplied}
                  currency={debt.currency}
                  paymentPlanConfig={config.payment.paymentPlan}
                  onConfigured={handlePlanConfigured}
                  isSubmitting={isProcessingPayment}
                  submitError={paymentError}
                  returnNotice={planReturnNotice}
                  initialFrequency={reviewPlanData ? reviewPlanData.frequency : linkPrefill.frequency}
                  initialStartDate={reviewPlanData ? toScheduleDate(reviewPlanData.startDate) : linkPrefill.start}
                  initialPaymentCount={reviewPlanData ? String(reviewPlanData.paymentCount) : linkPrefill.count}
                  decidedPlan={decidedPlan}
                  availablePlanMethods={planMethods}
                  initialPaymentMethod={reviewPlanData?.paymentMethod}
                  onOfferSubmitted={() => setDecidedPlan(null)}
                  assessmentReturned={linkPrefill.offer === 'assessed'}
                />
                {reviewPlanData && (
                  <PlanAuthoriseSheet
                    open={showPlanReview}
                    onClose={handleEditPlan}
                    totalAmount={reviewPlanData.totalAmount}
                    currency={debt.currency}
                    clientName={debt.originalCreditor}
                    plan={{ startDate: reviewPlanData.startDate, frequency: reviewPlanData.frequency, paymentCount: reviewPlanData.paymentCount }}
                    onAuthorise={() => executePlan('card')}
                    isProcessing={isProcessingPayment}
                    error={paymentError}
                  />
                )}
              </PaymentOptionView>
            ) : paymentTab === 'partial' && isPartialPaymentEnabled && tabVisibility.canPayPartial ? (
              <PaymentOptionView data-testid="payment-view-partial" title={t('payment.partialPayment')} onClose={tabVisibility.canPayFull ? () => setPaymentTab('full') : undefined}>
                <PartialPaymentTab
                  caseId={debt.id}
                  amount={debt.currentBalance}
                  creditApplied={debt.creditApplied}
                  currency={debt.currency}
                  allowDateSelect={config.payment.allowPartialPayLater}
                  maxDaysInFuture={config.payment.maxDaysInFuture}
                  onSelectPaymentMethod={handleSelectPaymentMethod}
                  methodSelector={methodSelector}
                  paymentMethod={paymentMethod}
                  onPay={handleProcessPayment}
                  isProcessing={isProcessingPayment}
                  canPay={methodUsable}
                  needsEmail={needsEmail}
                  payerEmail={payerEmail}
                  onPayerEmailChange={setPayerEmail}
                  emailValid={emailValid}
                  minPartialPaymentAmount={config.payment.minPartialPaymentAmount}
                  getAccessToken={getAccessToken}
                  initialAmountSubunits={linkPrefill.amount}
                />
              </PaymentOptionView>
            ) : tabVisibility.canPayFull ? (
              <PayInFullTab
                caseId={debt.id}
                amount={debt.currentBalance}
                creditApplied={debt.creditApplied}
                currency={debt.currency}
                allowFullPayLater={config.payment.allowFullPayLater}
                onSelectPaymentMethod={handleSelectPaymentMethod}
                methodSelector={methodSelector}
                paymentMethod={paymentMethod}
                onPay={handleProcessPayment}
                isProcessing={isProcessingPayment}
                canPay={methodUsable}
                maxDaysInFuture={config.payment.maxDaysInFuture}
                needsEmail={needsEmail}
                payerEmail={payerEmail}
                onPayerEmailChange={setPayerEmail}
                emailValid={emailValid}
                onChoosePlan={isPaymentPlanEnabled && tabVisibility.canCreatePlan ? () => setPaymentTab('plan') : undefined}
                onChoosePartial={isPartialPaymentEnabled && tabVisibility.canPayPartial ? () => setPaymentTab('partial') : undefined}
                planDiscountLabel={planDiscountLabel}
                optionsPlacement={config.payment.paymentOptionsPlacement}
              />
            ) : null}
          </TabsContent>

          <TabsContent value="breakdown">
            <DebtBreakdownTab
              debt={debt}
              history={
                customerTransactions.length > 0 && (
                  <TransactionsList transactions={customerTransactions} title={t('transaction.transactions')} currency={debt.currency} />
                )
              }
            />
          </TabsContent>
        </Tabs>

        {/* Multibanco Reference Modal */}
        {isMobile ? (
          <Drawer open={!!multibancoRef} onOpenChange={(open) => !open && setMultibancoRef(null)}>
            <DrawerContent className="h-full bg-card">
              <DrawerTitle className="sr-only">{t('multibanco.title')}</DrawerTitle>
              {multibancoRef && <MultibancoReference reference={multibancoRef} onClose={() => setMultibancoRef(null)} />}
            </DrawerContent>
          </Drawer>
        ) : (
          <Dialog open={!!multibancoRef} onOpenChange={(open) => !open && setMultibancoRef(null)}>
            <DialogContent className="max-w-2xl! h-full max-h-[95vh]! overflow-hidden no-scrollbar rounded-2xl">
              <DialogTitle className="sr-only">{t('multibanco.title')}</DialogTitle>
              {multibancoRef && <MultibancoReference reference={multibancoRef} onClose={() => setMultibancoRef(null)} />}
            </DialogContent>
          </Dialog>
        )}
      </div>
    </Layout>
  );
}
